import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const METRICS = [
  { value: 2.5, decimals: 1, prefix: "$", suffix: "B+", labelKey: "stats.l1" },
  { value: 150, decimals: 0, prefix: "", suffix: "+", labelKey: "stats.l2" },
  { value: 98, decimals: 0, prefix: "", suffix: "%", labelKey: "stats.l3" },
  { value: 7, decimals: 0, prefix: "", suffix: "", labelKey: "stats.l4" },
];

const Counter = ({ value, decimals, prefix, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState((0).toFixed(decimals));

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);

  return (
    <span ref={ref} className="font-display text-4xl sm:text-5xl lg:text-6xl text-gold-gradient">
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export default function Stats() {
  const { t } = useLang();

  return (
    <section className="relative border-y border-gold/25 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {METRICS.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={i > 0 ? "lg:border-l lg:border-gold/15 lg:pl-8" : ""}
            >
              <Counter value={m.value} decimals={m.decimals} prefix={m.prefix} suffix={m.suffix} />
              <p className="mt-3 text-sm text-slate-600">{t(m.labelKey)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
